import { omit } from 'lodash';
import * as yup from 'yup';
import { validatePerson } from '../../../../utils/validation/person';
import {
  RadioConvertUtils,
  RadioTrueFalseString,
} from '../../../../utils/radio/radio';
import { Person } from '../../../../global/entities/Person';
import {
  RelationType,
  SignAuthority,
} from '../../../../global/entities/BuyOrder';
import { hasFieldsWithValues } from '../../../../utils';

export const BoardMakeupErrors = {
  ErrorName: 'boardMakeup',
  Chairman: {
    mustHave: 'Styrelsen måste ha en ordförande om det är fler än en ledamot.',
    moreThanOne: 'Styrelsen kan inte ha mer än en ordförande.',
  },
  Member: {
    mustHave: 'Styrelsen måste innehålla minst en ordinarie ledamot.',
    moreThanFive: 'Styrelsen får inte ha fler än fem ledamöter.',
  },
  Substitute: {
    moreThanFive: 'Styrelsen får inte ha mer än fem suppleanter.',
    mustHave:
      'Suppleant måste ingå i styrelsen om den innehåller en ledamot och ordförande.',
  },
  notMinimum: 'Styrelsen måste ha minst två representanter.',
};

const personWithoutPhoneSchema = { ...omit(validatePerson, 'phoneNumber') };

export function representativeObjectHasValues(
  representative?: Partial<Person>,
): boolean {
  return hasFieldsWithValues(omit(representative, 'relationType'));
}

export const representativesValidationSchema = yup.object().shape({
  representatives: yup
    .array()
    .of(
      yup.object().shape({
        ...personWithoutPhoneSchema,
        relationType: yup.number().min(1, 'Du måste välja en funktion'),
      }),
    )
    /**
     * We omit 'relationType' because representatives always have it,
     * and it is meaningless to filter based on that value.
     * */
    .compact(
      // @ts-expect-error complains about null vs undefined
      (representative) => !representativeObjectHasValues(representative),
    ),
  officialCeo: yup.string(),
  ceo: yup.object().when('officialCeo', {
    is: (val) => RadioConvertUtils.strToBool(val),
    then: yup.object().shape(personWithoutPhoneSchema),
  }),
  signAuthority: yup.string().required(),
  signAuthorityUserDefined: yup.string().when('signAuthority', {
    is: (val) => val === SignAuthority.ABX,
    then: yup.string().trim().required('Företagsunderskrifter krävs'),
  }),
  boardMakeup: yup
    .mixed()
    .test({
      exclusive: false,
      params: {},
      message: BoardMakeupErrors.notMinimum,
      test() {
        const { total } = getBoardMakeup(this.parent.representatives);

        return total >= 2;
      },
    })
    .test({
      exclusive: false,
      params: {},
      message: BoardMakeupErrors.Chairman.moreThanOne,
      test() {
        const { chairman } = getBoardMakeup(this.parent.representatives);

        return chairman <= 1;
      },
    })
    .test({
      exclusive: false,
      params: {},
      message: BoardMakeupErrors.Member.mustHave,
      test() {
        return (
          getBoardMakeup(this.parent.representatives).regularBoardMember > 0
        );
      },
    })
    .test({
      exclusive: false,
      params: {},
      message: BoardMakeupErrors.Chairman.mustHave,
      test() {
        const { regularBoardMember, chairman } = getBoardMakeup(
          this.parent.representatives,
        );
        return !(regularBoardMember > 1 && chairman === 0);
      },
    })
    .test({
      exclusive: false,
      params: {},
      message: BoardMakeupErrors.Member.moreThanFive,
      test() {
        return (
          getBoardMakeup(this.parent.representatives).regularBoardMember <= 5
        );
      },
    })
    .test({
      exclusive: false,
      params: {},
      message: BoardMakeupErrors.Substitute.moreThanFive,
      test() {
        return (
          getBoardMakeup(this.parent.representatives).substituteBoardMember <= 5
        );
      },
    })
    .test({
      exclusive: false,
      params: {},
      message: BoardMakeupErrors.Substitute.mustHave,
      test() {
        const { chairman, regularBoardMember, substituteBoardMember } = getBoardMakeup(this.parent.representatives);

        if (chairman === 1 && regularBoardMember >= 2) {
          return true;
        }

        return !(
          chairman >= 1
          && regularBoardMember >= 1
          && substituteBoardMember === 0
        );
      },
    }),
});

export const RepresentativeLabel: Record<RelationType, string> = {
  [RelationType.BoardMember]: 'Ledamot',
  [RelationType.SubstituteBoardMember]: 'Suppleant',
  [RelationType.Chairman]: 'Ledamot och Ordförande',
};

export const OfficialCeoLabel: Record<RadioTrueFalseString, string> = {
  false: 'Nej',
  true: 'Ja, utse följande person:',
};

export function getBoardMakeup(representatives: Person[]) {
  const relationsCount = {
    /** *Both* Chairman, and BoardMember are considered board members */
    boardMember: 0,
    regularBoardMember: 0,
    substituteBoardMember: 0,
    chairman: 0,
    total: 0,
  };

  for (const rep of representatives) {
    switch (rep.relationType) {
      case RelationType.Chairman:
        relationsCount.chairman++;
        relationsCount.total++;
        relationsCount.boardMember++;
        break;
      case RelationType.BoardMember:
        relationsCount.regularBoardMember++;
        relationsCount.total++;
        relationsCount.boardMember++;
        break;
      case RelationType.SubstituteBoardMember:
        relationsCount.substituteBoardMember++;
        relationsCount.total++;
        break;
      default:
        break;
    }
  }

  return relationsCount;
}

export const signAuthorities: Record<SignAuthority, string> = {
  [SignAuthority.AB1]:
    'Firma tecknas av styrelsen (ordinarie styrelseledamöter)',
  [SignAuthority.AB2]: 'Firma tecknas var för sig av ledamoten och suppleanten',
  [SignAuthority.AB3]: 'Firma tecknas var för sig av ledamöterna',
  [SignAuthority.AB4]: 'Firma tecknas två i förening av ledamöterna',
  [SignAuthority.AB5]:
    'Firma tecknas av ledamoten/ledamöterna och suppleanten/suppleanterna var för sig',
  [SignAuthority.ABX]: 'Annan firmateckning enligt nedan:',
};

export const emptyPersonObject: Person & { numberShares?: number } = {
  firstName: '',
  lastName: '',
  socialSecurityNumber: '',
  address: '',
  postalCode: '',
  city: '',
  relationType: RelationType.None,
  numberShares: 0,
};

export const chairmanObject: Person = {
  ...emptyPersonObject,
  relationType: RelationType.Chairman,
};
